import type { MembersResponse } from "@contract/firm/members";
import { Anchor, Button, Group, Menu, Modal, Stack, Text } from "@mantine/core";
import * as React from "react";
import { Link } from "react-router";
import { track } from "../analytics/analytics";
import { ApiError } from "../api/client";
import * as membersApi from "../api/members";
import { useMe } from "../api/MeContext";
import { firmCanHaveTeam } from "../layout/nav";
import { DangerButton, SecondaryButton } from "../ui/buttons";
import { DataTable, type DataColumn } from "../ui/DataTable";
import { LinkAction } from "../ui/LinkAction";
import { MetaText } from "../ui/MetaText";
import { Notice } from "../ui/Notice";
import { PageHeader } from "../ui/PageHeader";
import { PageLoader } from "../ui/PageLoader";
import { SectionCard } from "../ui/SectionCard";
import { PANEL_SPACE } from "../ui/tokens";
import { InviteModal } from "./members/InviteModal";
import { actionLabel, creditsPeriodLabel, memberRows, ROLE_LABEL } from "./membersModel";

type Api = typeof membersApi;
type Row = ReturnType<typeof memberRows>[number];

/** Team Members (firm-model spec §10.2, admin only; Figma 14227:2213): the firm's people
 * and pending invites, role changes, removal, and the invite modal. */
export function MembersPage({ api = membersApi }: { api?: Api }) {
  const { state: meState } = useMe();
  const myUserId = meState.status === "ready" ? meState.me.userId : "";
  const canHaveTeam = meState.status === "ready" && firmCanHaveTeam(meState.me);
  const canBill = meState.status === "ready" && meState.me.capabilities.includes("manage_billing");
  const [data, setData] = React.useState<MembersResponse | null>(null);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [actionError, setActionError] = React.useState<string | null>(null);
  const [inviting, setInviting] = React.useState(false);
  const [removing, setRemoving] = React.useState<{ userId: string; name: string } | null>(null);
  const [nonce, setNonce] = React.useState(0);
  const reload = () => setNonce((n) => n + 1);

  React.useEffect(() => {
    let alive = true;
    api
      .fetchMembers()
      .then((d) => alive && (setData(d), setLoadError(null)))
      .catch((e: unknown) => alive && setLoadError(e instanceof Error ? e.message : "Couldn't load members."));
    return () => {
      alive = false;
    };
  }, [api, nonce]);

  const run = async (work: () => Promise<void>) => {
    setActionError(null);
    try {
      await work();
      reload();
    } catch (e) {
      setActionError(e instanceof ApiError ? e.message : "That didn't work. Try again.");
    }
  };

  if (loadError) return <Notice tone="danger">{loadError}</Notice>;
  if (!data) return <PageLoader />;

  if (data.seatsLeft === 0 && !canHaveTeam && data.members.length <= 1 && data.invites.length === 0) {
    return (
      <Stack gap={PANEL_SPACE.page} maw={720}>
        <PageHeader title="Team Members" />
        <SectionCard title="It's Just You for Now">
          <Text>{data.inviteBlockedReason} Your own account keeps working as it does today.</Text>
          {canBill ? (
            <Button component={Link} to="/billing?upgrade=team" w="fit-content">
              Upgrade to Team
            </Button>
          ) : null}
        </SectionCard>
      </Stack>
    );
  }

  const act = (row: Row, action: Row["actions"][number]) => {
    if (action.kind === "set_role") void run(() => api.changeRole(row.targetId, action.role));
    else if (action.kind === "remove") setRemoving({ userId: row.targetId, name: row.name || row.email });
    else if (action.kind === "resend") void run(() => api.resendInvite(row.targetId));
    else void run(() => api.revokeInvite(row.targetId));
  };

  const columns: ReadonlyArray<DataColumn<Row>> = [
    { key: "name", header: "Name", render: (r) => r.name },
    { key: "email", header: "Email", render: (r) => r.email },
    { key: "role", header: "Role", render: (r) => ROLE_LABEL[r.role] },
    { key: "status", header: "Status", render: (r) => r.status },
    { key: "last", header: "Last Active", render: (r) => r.lastActive },
    { key: "credits", header: "Credits Used", render: (r) => r.credits },
    {
      key: "actions",
      header: "Actions",
      render: (r) =>
        r.actions.length > 0 ? (
          <Menu position="bottom-end">
            <Menu.Target>
              <LinkAction label={`Manage ${r.name || r.email}`}>Manage</LinkAction>
            </Menu.Target>
            <Menu.Dropdown>
              {r.actions.map((action) => (
                <Menu.Item
                  key={action.kind}
                  color={action.kind === "remove" || action.kind === "revoke" ? "red" : undefined}
                  onClick={() => act(r, action)}
                >
                  {actionLabel(action)}
                </Menu.Item>
              ))}
            </Menu.Dropdown>
          </Menu>
        ) : null,
    },
  ];

  const period = creditsPeriodLabel(data);
  return (
    <Stack gap={PANEL_SPACE.page}>
      {data.inviteBlockedReason ? (
        <Notice tone="warning">
          {data.inviteBlockedReason}{" "}
          {canBill ? (
            <Anchor component={Link} to="/billing" c="inherit" td="underline">
              Plan & Billing
            </Anchor>
          ) : null}
        </Notice>
      ) : null}
      {actionError ? (
        <Notice tone="danger" onClose={() => setActionError(null)}>
          {actionError}
        </Notice>
      ) : null}
      <PageHeader
        title="Team Members"
        aside={
          <Button onClick={() => setInviting(true)} disabled={Boolean(data.inviteBlockedReason)}>
            Invite team member
          </Button>
        }
      />
      <Stack gap={PANEL_SPACE.field}>
        <DataTable label="Team members" columns={columns} rows={memberRows(data, myUserId)} rowKey={(r) => r.key} empty="No one here yet." />
        {period ? <MetaText>{period}</MetaText> : null}
      </Stack>

      <InviteModal opened={inviting} onClose={() => setInviting(false)} onInvited={reload} invite={api.inviteMembers} seatsLeft={data.seatsLeft} />

      <Modal opened={removing !== null} onClose={() => setRemoving(null)} title={`Remove ${removing?.name ?? ""}?`}>
        <Stack gap="md">
          <Text>Their access stops now. Their seat frees up. Work they logged stays with the firm.</Text>
          <Group justify="flex-end" gap={5}>
            <SecondaryButton onClick={() => setRemoving(null)}>Cancel</SecondaryButton>
            <DangerButton
              onClick={() => {
                const target = removing;
                setRemoving(null);
                if (target) {
                  track("web_member_removed");
                  void run(() => api.removeMember(target.userId));
                }
              }}
            >
              Remove
            </DangerButton>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
